import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { STEPS, GLP1_DOSES, translateStep } from "../lib/intakeSteps";
import { loadIntake, setAnswer, setIntent } from "../lib/intakeStore";
import TopNav from "../components/TopNav";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { getIntakeContent } from "../lib/intakeContent";

export default function IntakeRunner() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [tick, setTick] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);
  const [error, setError] = useState("");
  const [states, setStates] = useState([]);
  const { lang } = useLang();
  const ic = getIntakeContent(lang);

  // Initialize intent from URL on mount.
  useEffect(() => {
    const program = params.get("program");
    const product = params.get("product");
    const intake = loadIntake();
    if (!intake.answers._intent) {
      if (program) setIntent({ kind:"program", slug: program });
      else if (product) setIntent({ kind:"product", slug: product });
      else setIntent({ kind:"generic" });
      setTick(t=>t+1);
    }
    api.get("/states").then(r => setStates(r.data)).catch(()=>{});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const intake = loadIntake();
  const answers = intake.answers;
  const visibleSteps = useMemo(() => STEPS.filter(s => !(s.skipIf && s.skipIf(answers))).map(s => translateStep(s, lang)), [tick, lang]);  // eslint-disable-line react-hooks/exhaustive-deps
  const step = visibleSteps[stepIdx];

  if (!step) return <div className="aa-shell"><div>{ic.common.loading}</div></div>;
  const progress = Math.round(((stepIdx+1)/visibleSteps.length) * 100);

  const next = () => {
    setError("");
    if (stepIdx + 1 >= visibleSteps.length) nav("/intake/finalize");
    else setStepIdx(stepIdx + 1);
  };
  const back = () => stepIdx === 0 ? nav("/intake/start") : setStepIdx(stepIdx - 1);

  const set = (k, v) => { setAnswer(k, v); setTick(t=>t+1); };

  // ── Required-question validation for every step kind ──────────────────────
  const validate = () => {
    const k = step.key;
    const val = k ? answers[k] : undefined;

    // New intro and eligibility slides don't require validation
    if (step.kind === "glp1_intro" || step.kind === "eligibility_confirmation") return "";

    if (step.id === "s01_state" && !answers.state) return ic.validation.selectState;
    if (step.id === "s02_name") {
      if (!answers.first_name?.trim() || !answers.last_name?.trim()) return ic.validation.bothNamesRequired;
      if (!answers.email?.trim() || !/^\S+@\S+\.\S+$/.test(answers.email)) return ic.validation.validEmail;
    }
    if (step.id === "s03_dob") {
      if (!answers.dob) return ic.validation.enterDob;
      const d = new Date(answers.dob); const t = new Date();
      const age = t.getFullYear() - d.getFullYear() - ((t.getMonth() < d.getMonth() || (t.getMonth()===d.getMonth() && t.getDate()<d.getDate())) ? 1 : 0);
      if (age < 18) return ic.validation.mustBe18;
    }
    if (step.id === "s05_height_weight") {
      if (!answers.hft || answers.hin === undefined || answers.hin === "" || !answers.wt) {
        return ic.validation.heightWeightRequired;
      }
    }

    switch (step.kind) {
      case "radio":
      case "radio_long":
      case "yesno":
      case "yesno_text":
        if (!val) return ic.validation.pickOption;
        if (step.kind === "yesno_text" && val === "Yes" && !answers[step.textKey]?.trim()) {
          return ic.validation.shareDetails;
        }
        break;
      case "multi":
      case "multi_with_text":
        if (!Array.isArray(val) || val.length === 0) return ic.validation.selectAtLeastOne;
        break;
      case "dynamic_dose":
        if (!val) return ic.validation.pickDose;
        break;
      default: break;
    }
    return "";
  };

  const onContinue = () => {
    const err = validate();
    if (err) { setError(err); return; }
    next();
  };

  // Yes/No auto-advance dispatcher used by yesno + yesno_text steps.
  const shouldAutoAdvanceOn = (val) =>
    Array.isArray(step.autoAdvanceOn) && step.autoAdvanceOn.includes(val);

  // ── Render dispatcher ─────────────────────────────────────────────────────
  const renderStep = () => {
    switch (step.kind) {
      case "glp1_intro":
        return (
          <>
            <div className="aa-eyebrow" style={{textAlign:"center"}}>{ic.glp1Intro.eyebrow}</div>
            <h1 className="aa-h1" style={{textAlign:"center", fontSize:"2.2rem", marginBottom:"1rem"}}>
              {ic.glp1Intro.title}
            </h1>
            <h2 className="aa-h2" style={{textAlign:"center", fontSize:"1.2rem", color:"var(--muted)", fontWeight:400, marginBottom:"2rem"}}>
              {ic.glp1Intro.subtitle}
            </h2>
            <div style={{textAlign:"center", margin:"3rem auto"}}>
              <img
                src="https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/6a0f7e626b8ea8a8274679ba.png"
                alt="GLP-1 Medication"
                style={{
                  maxWidth:"400px",
                  width:"100%",
                  height:"auto",
                  borderRadius:"12px",
                  boxShadow:"0 8px 24px rgba(0,0,0,0.15)"
                }}
              />
            </div>
          </>
        );

      case "eligibility_confirmation":
        return (
          <>
            <div className="aa-eyebrow" style={{textAlign:"center", color:"var(--gold)"}}>{ic.eligibilityConfirmation.eyebrow}</div>
            <h1 className="aa-h1" style={{textAlign:"center", fontSize:"2rem", marginBottom:"1rem"}}>
              {ic.eligibilityConfirmation.title}
            </h1>
            <h2 className="aa-h2" style={{textAlign:"center", fontSize:"1.1rem", color:"var(--muted)", fontWeight:400, marginBottom:"2.5rem"}}>
              {ic.eligibilityConfirmation.subtitle}
            </h2>
            <div style={{textAlign:"center", margin:"2.5rem auto 2rem"}}>
              <img
                src="https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/6a027b5485b418d57ce2cce5.png"
                alt="Medical team"
                style={{
                  maxWidth:"480px",
                  width:"100%",
                  height:"auto",
                  display:"block",
                  margin:"0 auto"
                }}
              />
            </div>
          </>
        );

      case "state":
        return (
          <>
            <h1 className="aa-h1">{ic.steps.state.title}</h1>
            <p className="aa-sub">{ic.steps.state.subtitle}</p>
            <select className="aa-select" value={answers.state || ""} onChange={(e)=>set("state", e.target.value)} data-testid="intake-state">
              <option value="">{ic.steps.state.placeholder}</option>
              {states.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          </>
        );
      case "name":
        return (
          <>
            <h1 className="aa-h1">{ic.steps.name.title}</h1>
            <p className="aa-sub">{ic.steps.name.subtitle}</p>
            <div className="aa-row">
              <div><label className="aa-label">{ic.steps.name.firstName}</label>
                <input className="aa-input" value={answers.first_name||""} onChange={(e)=>set("first_name", e.target.value)} data-testid="intake-first-name"/></div>
              <div><label className="aa-label">{ic.steps.name.lastName}</label>
                <input className="aa-input" value={answers.last_name||""} onChange={(e)=>set("last_name", e.target.value)} data-testid="intake-last-name"/></div>
            </div>
            <div style={{marginTop:14}}>
              <label className="aa-label">{ic.steps.name.email}</label>
              <input className="aa-input" type="email" value={answers.email||""} onChange={(e)=>set("email", e.target.value)} data-testid="intake-email"/>
            </div>
          </>
        );
      case "dob": {
        // Calculate date 18 years ago for max constraint
        const today = new Date();
        const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
        const maxDate = eighteenYearsAgo.toISOString().split('T')[0];
        // Set reasonable min date (100 years ago)
        const hundredYearsAgo = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate());
        const minDate = hundredYearsAgo.toISOString().split('T')[0];

        const handleDobChange = (value) => {
          set("dob", value);
          // Check age immediately
          if (value) {
            const d = new Date(value);
            const t = new Date();
            const age = t.getFullYear() - d.getFullYear() - ((t.getMonth() < d.getMonth() || (t.getMonth()===d.getMonth() && t.getDate()<d.getDate())) ? 1 : 0);
            if (age < 18) {
              // Block immediately - redirect to ineligible page
              setTimeout(() => nav("/intake/ineligible"), 500);
            }
          }
        };

        return (
          <>
            <h1 className="aa-h1">{ic.steps.dob.title}</h1>
            <p className="aa-sub">{ic.steps.dob.subtitle}</p>
            <input
              className="aa-input"
              type="date"
              value={answers.dob||""}
              onChange={(e)=>handleDobChange(e.target.value)}
              min={minDate}
              max={maxDate}
              placeholder="MM/DD/YYYY"
              data-testid="intake-dob"
            />
          </>
        );
      }
      case "hw": {
        const inches = (parseInt(answers.hft||0)*12) + parseInt(answers.hin||0);
        const w = parseFloat(answers.wt||0);
        const bmi = (inches>0 && w>0) ? ((w/(inches*inches))*703).toFixed(1) : null;
        return (
          <>
            <h1 className="aa-h1">{ic.steps.heightWeight.title}</h1>
            <p className="aa-sub">{ic.steps.heightWeight.subtitle}</p>
            <div className="aa-row">
              <div><label className="aa-label">{ic.steps.heightWeight.feet}</label>
                <input className="aa-input" type="number" min="3" max="8" value={answers.hft||""} onChange={(e)=>set("hft", e.target.value)} data-testid="intake-hft"/></div>
              <div><label className="aa-label">{ic.steps.heightWeight.inches}</label>
                <input className="aa-input" type="number" min="0" max="11" value={answers.hin||""} onChange={(e)=>set("hin", e.target.value)} data-testid="intake-hin"/></div>
            </div>
            <div className="aa-row" style={{marginTop:10}}>
              <div><label className="aa-label">{ic.steps.heightWeight.currentWeight}</label>
                <input className="aa-input" type="number" min="60" max="700" value={answers.wt||""} onChange={(e)=>set("wt", e.target.value)} data-testid="intake-wt"/></div>
              <div><label className="aa-label">{ic.steps.heightWeight.goalWeight}</label>
                <input className="aa-input" type="number" value={answers.gwt||""} onChange={(e)=>set("gwt", e.target.value)} data-testid="intake-gwt"/></div>
            </div>
            {bmi && (
              <p className="aa-caption" data-testid="intake-bmi-caption" style={{marginTop:12}}>
                {ic.steps.heightWeight.bmi} <strong style={{color:"var(--gold)"}}>{bmi}</strong>
              </p>
            )}
          </>
        );
      }
      case "radio":
      case "radio_long": {
        const containerCls = step.kind === "radio_long" ? "aa-options aa-options-scroll" : "aa-options";
        return (
          <>
            <h1 className="aa-h1">{step.title}</h1>
            {step.subtitle && <p className="aa-sub">{step.subtitle}</p>}
            <div className={containerCls} style={step.kind==="radio_long"?{maxHeight:420, overflowY:"auto"}:undefined}>
              {step.options.map(opt => {
                const selected = answers[step.key] === opt.v;
                return (
                  <button key={opt.v} className={`aa-card ${selected?"selected":""}`}
                          onClick={()=>{ set(step.key, opt.v); if (step.autoAdvance) setTimeout(next, 180); }}
                          data-testid={`opt-${step.key}-${opt.v}`}>
                    <div className="aa-card-title">{opt.l || opt.v}</div>
                  </button>
                );
              })}
            </div>
          </>
        );
      }
      case "yesno": {
        const opts = step.options || [{v:"No", l:ic.common.no},{v:"Yes", l:ic.common.yes}];
        return (
          <>
            <h1 className="aa-h1">{step.title}</h1>
            {step.subtitle && <p className="aa-sub">{step.subtitle}</p>}
            <div className="aa-options">
              {opts.map(o => {
                const v = typeof o === "string" ? o : o.v;
                const l = typeof o === "string" ? o : (o.l || o.v);
                const selected = answers[step.key] === v;
                return (
                  <button key={v} className={`aa-card ${selected?"selected":""}`}
                          onClick={()=>{
                            set(step.key, v);
                            if (step.autoAdvance || shouldAutoAdvanceOn(v)) setTimeout(next, 180);
                          }}
                          data-testid={`opt-${step.key}-${v}`}>
                    <div className="aa-card-title">{l}</div>
                  </button>
                );
              })}
            </div>
          </>
        );
      }
      case "yesno_text": {
        const yn = answers[step.key];
        const opts = step.options || [{v:"Yes", l:ic.common.yes},{v:"No", l:ic.common.no}];
        return (
          <>
            <h1 className="aa-h1">{step.title}</h1>
            <div className="aa-options">
              {opts.map(o => {
                const v = typeof o === "string" ? o : o.v;
                const l = typeof o === "string" ? o : (o.l || o.v);
                const selected = yn === v;
                return (
                  <button key={v} className={`aa-card ${selected?"selected":""}`}
                          onClick={()=>{
                            set(step.key, v);
                            // No → auto-advance per spec; Yes → show textarea
                            if (shouldAutoAdvanceOn(v)) setTimeout(next, 180);
                          }}
                          data-testid={`opt-${step.key}-${v}`}>
                    <div className="aa-card-title">{l}</div>
                  </button>
                );
              })}
            </div>
            {yn === "Yes" && (
              <textarea className="aa-textarea" rows={3} placeholder={step.textLabel}
                        value={answers[step.textKey]||""} onChange={(e)=>set(step.textKey, e.target.value)}
                        data-testid={`text-${step.textKey}`} style={{marginTop:14}}/>
            )}
          </>
        );
      }
      case "multi":
      case "multi_with_text": {
        const arr = answers[step.key] || [];
        const toggle = (v, mutex) => {
          let cur = answers[step.key] || [];
          if (mutex) cur = cur.includes(v) ? [] : [v];
          else cur = cur.includes(v) ? cur.filter(x=>x!==v) : cur.filter(x => !(step.options.find(o=>o.v===x)?.mutex)).concat(v);
          set(step.key, cur);
        };
        return (
          <>
            <h1 className="aa-h1">{step.title}</h1>
            {step.subtitle && <p className="aa-sub">{step.subtitle}</p>}
            <div className="aa-options">
              {step.options.map(opt => {
                const selected = arr.includes(opt.v);
                return (
                  <button key={opt.v} className={`aa-card ${selected?"selected":""}`}
                          onClick={()=>toggle(opt.v, opt.mutex)}
                          data-testid={`opt-${step.key}-${opt.v}`}
                          style={{display:"flex", alignItems:"center"}}>
                    <span className="aa-check"/>
                    <span className="aa-card-title" style={{fontWeight:500}}>{opt.l || opt.v}</span>
                  </button>
                );
              })}
            </div>
            {step.kind === "multi_with_text" && (
              <textarea className="aa-textarea" rows={2} placeholder={step.textLabel}
                        value={answers[step.textKey]||""} onChange={(e)=>set(step.textKey, e.target.value)}
                        data-testid={`text-${step.textKey}`} style={{marginTop:14}}/>
            )}
          </>
        );
      }
      case "dynamic_dose": {
        const med = answers[step.dependsOn];
        const doseOptions = (GLP1_DOSES[med] || []).map(d => ({ v:d, l:d }));
        return (
          <>
            <h1 className="aa-h1">{step.title}</h1>
            {doseOptions.length === 0 && (
              <p className="aa-sub">{lang === "es" ? "Por favor regresa y elige un medicamento primero." : "Please go back and pick a medication first."}</p>
            )}
            <div className="aa-options">
              {doseOptions.map(opt => {
                const selected = answers[step.key] === opt.v;
                return (
                  <button key={opt.v} className={`aa-card ${selected?"selected":""}`}
                          onClick={()=>set(step.key, opt.v)}
                          data-testid={`opt-${step.key}-${opt.v}`}>
                    <div className="aa-card-title">{opt.l}</div>
                  </button>
                );
              })}
            </div>
          </>
        );
      }
      case "interstitial":
        return (
          <>
            {step.eyebrow && <div className="aa-eyebrow">{ic.goodNews.eyebrow}</div>}
            <h1 className="aa-h1">{ic.goodNews.title}</h1>
            <p className="aa-sub">{ic.goodNews.body}</p>
            {step.id === "s08_good_news" && (() => {
              const h = (parseInt(answers.hft||0)*12) + parseInt(answers.hin||0);
              const w = parseFloat(answers.wt||0);
              const b = (h>0&&w>0) ? ((w/(h*h))*703).toFixed(1) : null;
              return b ? (
                <div className="aa-card" data-testid="intake-bmi-card" style={{marginTop:16, cursor:"default"}}>
                  <div className="aa-card-sub">{ic.steps.heightWeight.bmi}</div>
                  <div style={{fontFamily:"var(--font-display)", fontSize:"2.4rem", color:"var(--gold)"}}>{b}</div>
                </div>
              ) : null;
            })()}
          </>
        );
      case "great_choice":
        return (
          <div data-testid="intake-great-choice">
            <div className="aa-eyebrow">{ic.greatChoice.title}</div>
            <h1 className="aa-h1">{lang === "es" ? "Recomendación personalizada" : "Personalized recommendation"}</h1>
            <ul style={{listStyle:"none", padding:0, margin:"22px 0 10px"}}>
              {ic.trust.map((t,i)=>(
                <li key={i} style={{display:"flex", alignItems:"flex-start", gap:12, marginBottom:14}}>
                  <span style={{
                    width:28, height:28, flexShrink:0, borderRadius:"50%",
                    background:"var(--gold)", color:"var(--black)",
                    display:"inline-flex", alignItems:"center", justifyContent:"center",
                    fontWeight:700,
                  }}>✓</span>
                  <span style={{lineHeight:1.5}}>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      default: return null;
    }
  };

  // ── Button visibility - show on all slides ──
  const showButtons = true;

  return (
    <>
      <TopNav minimal />
      <div className="aa-shell" style={{paddingTop: "100px"}}>
        <div className="aa-progress" data-testid="intake-progress">
          <div className={progress>0?"active":""} />
          <div className={progress>33?"active":""} />
          <div className={progress>66?"active":""} />
        </div>
        {renderStep()}
        {error && <div className="aa-error" data-testid="intake-error">{error}</div>}

        {/* Show Back and Continue buttons on all slides */}
        {showButtons && (
          <div className="aa-bottom" style={{
            display:"flex",
            gap:"12px",
            alignItems:"center",
            justifyContent: "center"
          }}>
            {stepIdx > 0 && step.kind !== "eligibility_confirmation" && (
              <button className="aa-btn-ghost" onClick={back} data-testid="intake-back">← {ic.common.back}</button>
            )}
            <button className="aa-btn" onClick={onContinue} data-testid="intake-continue" style={{
              minWidth: step.kind === "eligibility_confirmation" ? "280px" : undefined
            }}>
              {step.kind === "glp1_intro" ? ic.common.seeIfEligible :
               step.kind === "eligibility_confirmation" ? ic.common.clickToContinue :
               ic.common.continue}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
